import React from "react";
import { motion } from "framer-motion/dist/framer-motion";
import { useTranslation } from "react-i18next/";
import { useSnapshot } from "valtio";
import { useSelector, useDispatch } from "react-redux";
import {
    DynamicTable,
    customColumnProps,
} from "@maherunlocker/custom-react-table";
import { ToastContainer } from "react-toastify";

import { showSuccessToast } from "@components/Common/SuccessToast";
import { showErrorToast } from "@components/Common/ErrorToast";

import PageTitle from "@components/Common/PageTitle";
import MessageInfo from "@components/Common/MessageInfo";
import { StyledBreadcrumb } from "@aureskonnect/react-ui";
import { store as userStore } from "@components/VerticalLayout/store";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { fetchGlobal } from "../../../../api/hooks";
import {
    SETTING_SCANTO,
    MASTER_SHOP_ID,
    DEFAULT_LANGUAGE_CODE,
} from "@constants/index";
import {
    resetScanTo,
    setActivity,
    setBanner,
    setOffers,
    setActions,
    setMyOrder,
    setAdvertising,
    setMiscellaneous,
    setPaymentOptions,
    setServerCallOptions,
    ScanToState,
} from "../../../../redux/scanToSlice";
import { RootState } from "../../../../redux/store";

import { ModalWrapper } from "./ModalWrapper";
import { ModalContentWrapper } from "./ModalContentWrapper";
import { ShopRowData } from "./types";

// DynamicTable's arrayOfCustomColumns descriptors for the shop list. The
// titles are translated at render time via t(titleKey); the dataKey is the
// French JSON field name the backend always returns regardless of locale.
const SHOP_DATA_COLUMNS = [
    { titleKey: "Shop", dataKey: "Boutique", testid: "scan-to-shop-cell" },
    {
        titleKey: "Address",
        dataKey: "Adresse",
        testid: "scan-to-address-cell",
    },
    {
        titleKey: "Postal code",
        dataKey: "Code Postal",
        testid: "scan-to-postal-code-cell",
    },
    { titleKey: "City", dataKey: "Ville", testid: "scan-to-city-cell" },
    { titleKey: "Country", dataKey: "Pays", testid: "scan-to-country-cell" },
] as const;

// Strip in-memory blob: URLs from a config tree before sending it to the
// server. The server resolves uploaded files by filename + req.files, so any
// `blob:` value left in the JSON is dead weight. Walking the structure (rather
// than regex over the serialized string) avoids matching `blob:` substrings
// that happen to live inside unrelated text fields.
export function stripBlobUrls<T>(value: T): T {
    if (typeof value === "string") {
        return (value.startsWith("blob:") ? "" : value) as T;
    }
    if (Array.isArray(value)) {
        return (value.map(stripBlobUrls) as unknown) as T;
    }
    if (value !== null && typeof value === "object") {
        const result: Record<string, unknown> = {};
        for (const [key, val] of Object.entries(
            value as Record<string, unknown>
        )) {
            result[key] = stripBlobUrls(val);
        }
        return result as T;
    }
    return value;
}

export function ScanTo() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { oneShop, franchiseID, shopID } = useSnapshot(userStore);
    const selectedAccount = useSelector(
        (state: RootState) => state.global.selectedAccount
    );
    const i18nextLng =
        localStorage.getItem("i18nextLng") || DEFAULT_LANGUAGE_CODE;
    const uuidUser = localStorage.getItem("uuidUser") || "";

    const [
        isConfigurationModalOpened,
        setIsConfigurationModalOpened,
    ] = React.useState<boolean>(false);
    const [selectedShop, setSelectedShop] = React.useState<ShopRowData | null>(
        null
    );
    const shopId = oneShop ? shopID : 0;
    const [dataIsUpdated, setDataIsUpdated] = React.useState<boolean | number>(
        false
    );
    const [filterActive, setLocalFilterActive] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);

    // Per-modal-instance image registries — scoped to component lifecycle
    // so they cannot leak across users/tabs the way module-level singletons did.
    const pendingFilesRef = React.useRef<Map<string, File>>(new Map());
    const deletedImagesRef = React.useRef<Set<string>>(new Set());

    const scanToConfig = useSelector(
        (state: RootState) => state.scanTo
    ) as ScanToState;

    const handleSave = async () => {
        const shopId =
            selectedShop?.shopId ??
            selectedShop?.Id ??
            selectedShop?.uid ??
            MASTER_SHOP_ID;

        setIsSaving(true);
        try {
            const url = `${process.env.REACT_APP_API_V2_URL}/scanTo_servio/upsert/${shopId}/${franchiseID}/${SETTING_SCANTO}`;
            const { isEdited, activeTab, ...configData } = scanToConfig;

            // Strip in-memory blob: URLs — server uses filenames + req.files
            const configJson = JSON.stringify(stripBlobUrls(configData));

            // Build multipart/form-data: data (JSON) + image files + deleted list
            const formData = new FormData();
            formData.append("data", configJson);

            for (const [, file] of pendingFilesRef.current) {
                formData.append("images", file, file.name);
            }

            if (deletedImagesRef.current.size > 0) {
                formData.append(
                    "deletedImages",
                    JSON.stringify([...deletedImagesRef.current])
                );
            }

            await fetchGlobal<any>(url, {
                method: "POST",
                body: formData,
            });

            pendingFilesRef.current.clear();
            deletedImagesRef.current.clear();

            showSuccessToast(t("Registration successfully"));
            setIsConfigurationModalOpened(false);
            setDataIsUpdated(true);
        } catch (err) {
            const detail =
                err instanceof Error && err.message ? err.message : "";
            showErrorToast(
                `${t("Failed to save configuration")}${
                    detail ? ` — ${detail}` : "!"
                }`
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenConfiguration = React.useCallback(
        async (shop: ShopRowData) => {
            const shopId =
                shop?.shopId ?? shop?.Id ?? shop?.uid ?? MASTER_SHOP_ID;

            setIsLoading(true);
            dispatch(resetScanTo());

            try {
                const url = `${process.env.REACT_APP_API_V2_URL}/scanTo_servio/${shopId}/${franchiseID}/${SETTING_SCANTO}`;
                const response = await fetchGlobal<any>(url);

                // Handle wrapped responses: { data: {...} } or direct object
                const data = response?.data || response;

                if (data && !data.error) {
                    if (data.activity) dispatch(setActivity(data.activity));
                    if (data.banner) dispatch(setBanner(data.banner));
                    if (data.offers) dispatch(setOffers(data.offers));
                    if (data.actions) dispatch(setActions(data.actions));
                    if (data.myOrder) dispatch(setMyOrder(data.myOrder));
                    if (data.advertising)
                        dispatch(setAdvertising(data.advertising));
                    if (data.miscellaneousSettings)
                        dispatch(setMiscellaneous(data.miscellaneousSettings));
                    if (data.paymentOptions)
                        dispatch(setPaymentOptions(data.paymentOptions));
                    if (data.serverCallOptions)
                        dispatch(setServerCallOptions(data.serverCallOptions));
                }

                setSelectedShop(shop);
                setIsConfigurationModalOpened(true);
            } catch (err) {
                const detail =
                    err instanceof Error && err.message ? err.message : "";
                showErrorToast(
                    `${t("Failed to load configuration")}${
                        detail ? ` — ${detail}` : "!"
                    }`
                );
            } finally {
                setIsLoading(false);
            }
        },
        [franchiseID, dispatch, t]
    );

    const arrayOfCustomColumns: customColumnProps[] = React.useMemo(
        () => [
            ...SHOP_DATA_COLUMNS.map((col, i) => ({
                indexOFColumn: i + 1,
                columnName: t(col.titleKey),
                customJsx: (e: { selectedRow: ShopRowData }) => (
                    <span data-testid={col.testid}>
                        {e.selectedRow?.[col.dataKey] as React.ReactNode}
                    </span>
                ),
            })),
            {
                indexOFColumn: SHOP_DATA_COLUMNS.length + 1,
                columnName: t("Actions"),
                customJsx: (e: { selectedRow: ShopRowData }) => (
                    <div className="d-flex justify-content-center">
                        <CustomMainColorButton
                            data-testid="scan-to-configure-btn"
                            className="scan-to_btn_configure"
                            rounded
                            variant="primary"
                            disabled={isLoading}
                            onClick={() =>
                                handleOpenConfiguration(e.selectedRow)
                            }
                        >
                            {t("Configure")}
                        </CustomMainColorButton>
                    </div>
                ),
            },
        ],
        [t, isLoading, handleOpenConfiguration]
    );

    return (
        <React.Fragment>
            <PageTitle title={"Scan to"} data-testid="scan-to-page-title" />
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
                className="page-content__clz home-page-content__clz"
                data-testid="scan-to-page-container"
            >
                <StyledBreadcrumb
                    data-testid="scan-to-breadcrumb"
                    items={[
                        {
                            item: "Settings".toUpperCase(),
                            link: `${process.env.REACT_APP_PORTAIL_URL}`,
                        },
                        {
                            item: selectedAccount?.toUpperCase() || "",
                            link: oneShop
                                ? "/setting/customer-account"
                                : "/compte",
                        },
                        {
                            item: t("Application").toUpperCase(),
                            link: "applications",
                        },
                        {
                            item: t("Scan to Start").toUpperCase(),
                            link: "/scan-to-start",
                        },
                        {
                            item: t("Scan to").toUpperCase(),
                            link: "#",
                        },
                    ]}
                />
                <MessageInfo
                    data-testid="scan-to-message-info"
                    message={t(
                        "Configure the Scan to application settings including activity, banner, offers, actions, order, advertising and footer."
                    )}
                />
                <DynamicTable
                    // Force a remount on language change. The library's
                    // internal columns useMemo has deps [apiResultColumns]
                    // only, so it doesn't pick up the new t()-translated
                    // headers we pass via arrayOfCustomColumns. Remounting
                    // re-runs that memo with the up-to-date arrayOfCustomColumns.
                    key={i18n.language}
                    data-testid="scan-to-shop-table"
                    requestHeader={{
                        "Accept-Language": i18nextLng,
                    }}
                    url={`${process.env.REACT_APP_API_V2_URL}/scanTo_servio?shopId=${shopId}&franchiseId=${franchiseID}&uuidUser=${uuidUser}&type=${SETTING_SCANTO}`}
                    canSelect
                    showGlobalFilter
                    showFilter
                    filterActive={filterActive}
                    setLocalFilterActive={setLocalFilterActive}
                    arrayOfCustomColumns={arrayOfCustomColumns}
                    setDataIsUpdated={setDataIsUpdated}
                    dataIsUpdated={dataIsUpdated}
                    minHeight="520px"
                    maxHeight="520px"
                />
            </motion.div>

            {isConfigurationModalOpened && (
                <ModalWrapper
                    data-testid="scan-to-configuration-modal"
                    isConfigurationModalOpened={isConfigurationModalOpened}
                    setIsConfigurationModalOpened={
                        setIsConfigurationModalOpened
                    }
                    onSave={handleSave}
                    isLoading={isSaving}
                >
                    <ModalContentWrapper
                        rowData={selectedShop ?? undefined}
                        pendingFilesRef={pendingFilesRef}
                        deletedImagesRef={deletedImagesRef}
                    />
                </ModalWrapper>
            )}
            <ToastContainer limit={1} />
        </React.Fragment>
    );
}
